import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Dialog } from '../ui/Dialog/Dialog';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Checkbox } from '../atoms/Checkbox/Checkbox';
import { Input } from '../atoms/Input/Input';
import { Select } from '../atoms/Select/Select';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { Textarea } from '../atoms/Textarea/Textarea';
import { localToday } from '../livestock/livestockDeceased';
import {
  BUDGET_CATEGORIES,
  createExpense,
  createRecurring,
  updateExpense,
  updateRecurring,
  type BudgetCategory,
  type BudgetExpense,
  type RecurringExpense,
} from '../../services/budgetService';
import { centsToInput, parseAmountToCents } from './budgetFormat';
import { useMoney } from './useMoney';
import './BudgetPageParts.css';

/** What the dialog edits: a new entry (one-off or recurring), an expense, or a recurring cost. */
export type ExpenseDialogTarget =
  | { kind: 'new'; recurring: boolean }
  | { kind: 'expense'; expense: BudgetExpense }
  | { kind: 'recurring'; recurring: RecurringExpense };

export interface ExpenseDialogProps {
  aquariumId: string;
  isOpen: boolean;
  target: ExpenseDialogTarget;
  onClose: () => void;
  /** `recurring`: a recurring cost was saved; `created`: it is new. */
  onSaved: (result: { amountCents: number; recurring: boolean; created: boolean; spentOn: string | null }) => void;
}

const MAX_INTERVAL = 60;

/**
 * Adds or edits an expense — category, amount, date, note — or a recurring
 * cost: the same, repeated every N months (1 = monthly) from its first date,
 * optionally until an end date. Recurring costs are booked as expenses up to today.
 */
export function ExpenseDialog({ aquariumId, isOpen, target, onClose, onSaved }: ExpenseDialogProps) {
  const { t } = useTranslation();
  const money = useMoney();
  // Read on open only — the form must not refill when the currency loads mid-edit.
  const moneyRef = useRef(money);
  moneyRef.current = money;
  const [today] = useState(localToday);
  const [category, setCategory] = useState<BudgetCategory>('salt');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(today);
  const [note, setNote] = useState('');
  const [repeats, setRepeats] = useState(false);
  const [intervalMonths, setIntervalMonths] = useState('1');
  const [endDate, setEndDate] = useState('');
  const [errors, setErrors] = useState<{ amount?: string; date?: string; interval?: string; endDate?: string }>({});
  const [saveError, setSaveError] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fill the form whenever the dialog opens (for a new entry: keep the last category).
  useEffect(() => {
    if (!isOpen) return;
    const source = target.kind === 'expense' ? target.expense : target.kind === 'recurring' ? target.recurring : null;
    if (source) setCategory(source.category);
    setAmount(source ? centsToInput(source.amountCents, moneyRef.current.language, moneyRef.current.fractionDigits) : '');
    setNote(source?.note ?? '');
    setDate(target.kind === 'expense' ? target.expense.spentOn : target.kind === 'recurring' ? target.recurring.startOn : today);
    setRepeats(target.kind === 'recurring' || (target.kind === 'new' && target.recurring));
    setIntervalMonths(target.kind === 'recurring' ? String(target.recurring.intervalMonths) : '1');
    setEndDate(target.kind === 'recurring' ? (target.recurring.endOn ?? '') : '');
    setErrors({});
    setSaveError(false);
  }, [isOpen, target, today]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const cents = parseAmountToCents(amount, money.fractionDigits);
    const months = Number(intervalMonths);
    const next: typeof errors = {};
    if (cents === null) next.amount = t(money.fractionDigits === 0 ? 'budget.form.amountErrorWhole' : 'budget.form.amountError');
    if (!date || (!repeats && date > today)) next.date = t('budget.form.dateError');
    if (repeats && (!Number.isInteger(months) || months < 1 || months > MAX_INTERVAL)) next.interval = t('budget.form.intervalError', { max: MAX_INTERVAL });
    if (repeats && endDate && endDate < date) next.endDate = t('budget.form.endDateError');
    setErrors(next);
    if (Object.keys(next).length > 0 || cents === null) return;
    setSaving(true);
    setSaveError(false);
    try {
      const base = { category, amount_cents: cents, note: note.trim() || null };
      if (repeats) {
        const input = { ...base, interval_months: months, start_on: date, end_on: endDate || null };
        if (target.kind === 'recurring') await updateRecurring(aquariumId, target.recurring.id, input);
        else await createRecurring(aquariumId, input);
        onSaved({ amountCents: cents, recurring: true, created: target.kind !== 'recurring', spentOn: null });
      } else {
        const input = { ...base, spent_on: date };
        const saved = target.kind === 'expense' ? await updateExpense(aquariumId, target.expense.id, input) : await createExpense(aquariumId, input);
        onSaved({ amountCents: saved.amountCents, recurring: false, created: target.kind === 'new', spentOn: saved.spentOn });
      }
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  const titleKey =
    target.kind === 'recurring' ? 'budget.form.editRecurringTitle' : target.kind === 'expense' ? 'budget.form.editTitle' : 'budget.form.addTitle';
  const submitKey = target.kind === 'new' ? 'budget.form.add' : 'budget.form.save';

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title={t(titleKey)} size="sm" data-testid="expense-dialog">
      <form onSubmit={submit} noValidate>
        <Stack gap="4">
          {saveError && <Alert>{t('budget.form.saveError')}</Alert>}
          {target.kind === 'expense' && target.expense.recurringId && <Text size="sm" color="secondary">{t('budget.form.occurrenceHint')}</Text>}
          {target.kind === 'recurring' && <Text size="sm" color="secondary">{t('budget.form.recurringEditHint')}</Text>}
          <Select
            id="expense-category"
            label={t('budget.form.category')}
            value={category}
            options={BUDGET_CATEGORIES.map((c) => ({ value: c, label: String(t(`budget.categories.${c}`)) }))}
            onChange={(e) => setCategory(e.target.value as BudgetCategory)}
            disabled={saving}
          />
          <Input
            id="expense-amount"
            label={t('budget.form.amount', { currency: money.symbol })}
            helperText={t(money.fractionDigits === 0 ? 'budget.form.amountHelpWhole' : 'budget.form.amountHelp')}
            inputMode="decimal"
            autoComplete="off"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setErrors((prev) => ({ ...prev, amount: undefined }));
            }}
            error={errors.amount}
            required
            disabled={saving}
          />
          {target.kind === 'new' && (
            <Checkbox
              id="expense-repeats"
              label={t('budget.form.repeats')}
              checked={repeats}
              onChange={(e) => {
                setRepeats(e.target.checked);
                setErrors({});
              }}
              disabled={saving}
            />
          )}
          <Input
            id="expense-date"
            type="date"
            label={t(repeats ? 'budget.form.startDate' : 'budget.form.date')}
            helperText={repeats ? t('budget.form.startDateHelp') : undefined}
            value={date}
            max={repeats ? undefined : today}
            onChange={(e) => {
              setDate(e.target.value);
              setErrors((prev) => ({ ...prev, date: undefined, endDate: undefined }));
            }}
            error={errors.date}
            required
            disabled={saving}
          />
          {repeats && (
            <>
              <Input
                id="expense-interval"
                type="number"
                inputMode="numeric"
                min={1}
                max={MAX_INTERVAL}
                step={1}
                label={t('budget.form.interval')}
                helperText={t('budget.form.intervalHelp')}
                value={intervalMonths}
                onChange={(e) => {
                  setIntervalMonths(e.target.value);
                  setErrors((prev) => ({ ...prev, interval: undefined }));
                }}
                error={errors.interval}
                required
                disabled={saving}
              />
              <Input
                id="expense-end-date"
                type="date"
                label={t('budget.form.endDate')}
                helperText={t('budget.form.endDateHelp')}
                value={endDate}
                min={date || undefined}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setErrors((prev) => ({ ...prev, endDate: undefined }));
                }}
                error={errors.endDate}
                disabled={saving}
              />
            </>
          )}
          <Textarea
            id="expense-note"
            label={t('budget.form.note')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('budget.form.notePlaceholder')}
            rows={2}
            maxLength={500}
            disabled={saving}
          />
          <div className="budget-dialog__actions">
            <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>
              {t('budget.form.cancel')}
            </Button>
            <Button type="submit" isLoading={saving}>
              {t(submitKey)}
            </Button>
          </div>
        </Stack>
      </form>
    </Dialog>
  );
}
